import { flip, shift } from '@floating-ui/react-dom';
import {
  Navbar2Element,
  NavbarDropdown,
  NavbarDropdownMenuColumn,
  NavbarDropdownMenuItem,
} from '@typedream/core';
import { findNodePath, TNode } from '@udecode/plate-core';
import { useMachine } from '@xstate/react';
import { useEffect, useState } from 'react';
import { assign } from 'xstate';
import { cloneDeep } from 'lodash';
import { Floating } from '../../../../components/Floating';
import { changeNavbar } from '../../../../nodes/navbar/transformers';
import { OverlayPortal } from '../../../../overlay/OverlayPortal';
import { useTPlateEditorRef } from '../../../../plate';
import {
  TextEditorOpenNavbarDropdownContainerEvent,
  ToolbarNavbarDropdownColumnDropEvent,
  ToolbarNavbarDropdownMenuEmptyColumnAddEvent,
  ToolbarNavbarDropdownMenuItemDropEvent,
  ToolbarNavbarDropdownMenuItemEvent,
} from '../../../../types';
import BottomOptions from './Dropdown/BottomOptions';
import { ColumnItemSettingToolbar } from './Dropdown/ColumnItemSetting';
import { ColumnSettingToolbar } from './Dropdown/ColumnSetting';
import { makeInitialMenuColumnValue, makeInitialMenuItemValue } from './dropdownInitialValues';
import { navbarDropdownContainerFSM } from './finiteStateMachines/NavbarDropdownContainer.fsm';
import {
  getSelectedNavbarDropdownItems,
  isColumnEmpty,
  updateArrayItemByPosition,
  updateDropdownMenuItem,
} from './utils';

export function NavbarDropdownContainerOverlayToolbar() {
  const [domNode, setDomNode] = useState<HTMLElement | null>(null);
  const [itemDomNode, setItemDomNode] = useState<HTMLElement | null>(null);

  const editor = useTPlateEditorRef();

  // TODO: Move to the FSM's context
  const [navbarReference, setNavbarReference] = useState<Navbar2Element | undefined>();

  const [machineState, machineSend, machineService] = useMachine(navbarDropdownContainerFSM, {
    actions: {
      assignColumnItemTitleToContext: assign((context, event) => {
        if (
          context.dataNavbar === undefined
          || context.menuType === undefined
          || context.menuType === 'brand'
          || context.dataNavbar?.[context.menuType] === undefined) return {};

        // Get and check the current selected column
        const assignmentRef = getSelectedNavbarDropdownItems(context);
        if (assignmentRef === undefined) return {};

        const { columnItem } = assignmentRef;

        if (columnItem === undefined) return {};

        const newColumnItem: NavbarDropdownMenuItem = {
          ...columnItem,
        };

        if ('newTitle' in event.payload) {
          newColumnItem.title = event.payload.newTitle;
        }

        if ('newColor' in event.payload) {
          newColumnItem.titleColor = event.payload.newColor;
        }

        const updatedMenuItem = updateDropdownMenuItem({
          context,
          newDropdownMenuItem: newColumnItem,
        });

        if (updatedMenuItem === undefined) return {};

        return {
          dataNavbar: {
            ...context.dataNavbar,
            [context.menuType]: updatedMenuItem,
          },
        };
      }),
      assignColumnItemDescriptionToContext: assign((context, event) => {
        if (
          context.dataNavbar === undefined
          || context.menuType === undefined
          || context.menuType === 'brand'
          || context.dataNavbar?.[context.menuType] === undefined) return {};

        // Get and check the current selected column
        const assignmentRef = getSelectedNavbarDropdownItems(context);
        if (assignmentRef === undefined) return {};

        const { columnItem } = assignmentRef;

        if (columnItem === undefined) return {};

        const newDropdownMenuItem: NavbarDropdownMenuItem = {
          ...columnItem,
        };

        if ('newDescription' in event.payload) {
          newDropdownMenuItem.description = event.payload.newDescription;
        }

        if ('newColor' in event.payload) {
          newDropdownMenuItem.descriptionColor = event.payload.newColor;
        }

        const updatedMenuItem = updateDropdownMenuItem({
          context,
          newDropdownMenuItem,
        });

        if (updatedMenuItem === undefined) return {};

        return {
          dataNavbar: {
            ...context.dataNavbar,
            [context.menuType]: updatedMenuItem,
          },
        };
      }),
      assignColumnItemLinkToContext: assign((context, event) => {
        if (
          context.dataNavbar === undefined
          || context.menuType === undefined
          || context.menuType === 'brand'
          || context.dataNavbar?.[context.menuType] === undefined) return {};

        // Get and check the current selected column
        const assignmentRef = getSelectedNavbarDropdownItems(context);
        if (assignmentRef === undefined) return {};

        const { columnItem } = assignmentRef;

        if (columnItem === undefined) return {};

        const newData: Pick<NavbarDropdownMenuItem, 'openInNewTab' | 'pageId' | 'url' | 'internalLink'> = {};

        if (event.type === 'ON_CHANGE_COLUMN_ITEM_LINK') {
          newData.url = event.payload.pageID ? `/${event.payload.pagePath}` : event.payload.url;
          newData.pageId = event.payload.pageID;
          newData.internalLink = event.payload.pageID ? true : undefined;
        }

        if (event.type === 'ON_CHANGE_COLUMN_ITEM_LINK_OPEN_NEW_TAB') {
          newData.openInNewTab = event.payload.openNewTab;
        }

        const newDropdownMenuItem: NavbarDropdownMenuItem = {
          ...columnItem,
          ...newData,
        };

        const updatedMenuItem = updateDropdownMenuItem({
          context,
          newDropdownMenuItem,
        });

        if (updatedMenuItem === undefined) return {};

        return {
          dataNavbar: {
            ...context.dataNavbar,
            [context.menuType]: updatedMenuItem,
          },
        };
      }),
      assignColumnItemIconToContext: assign((context, event) => {
        if (
          context.dataNavbar === undefined
          || context.menuType === undefined
          || context.menuType === 'brand'
          || context.dataNavbar?.[context.menuType] === undefined) return {};

        const newIcon = event.type === 'done.invoke.uploadImage'
          ? (event.data as string) : event.payload.icon;

        // Get and check the current selected column
        const assignmentRef = getSelectedNavbarDropdownItems(context);
        if (assignmentRef === undefined) return {};

        const { columnItem } = assignmentRef;

        if (columnItem === undefined) return {};

        const newDropdownMenuItem: NavbarDropdownMenuItem = {
          ...columnItem,
          icon: newIcon,
        };

        const updatedMenuItem = updateDropdownMenuItem({
          context,
          newDropdownMenuItem,
        });

        if (updatedMenuItem === undefined) return {};

        return {
          dataNavbar: {
            ...context.dataNavbar,
            [context.menuType]: updatedMenuItem,
          },
        };
      }),
      assignColumnItemTypeToContext: assign((context, event) => {
        if (
          context.dataNavbar === undefined
          || context.menuType === undefined
          || context.menuType === 'brand'
          || context.menuIndex === undefined
          || context.dataNavbar?.[context.menuType] === undefined
          || context?.selectedColumnItemIdx === undefined
        ) return {};

        // Get and check the current selected column
        const assignmentRef = getSelectedNavbarDropdownItems(context);
        if (assignmentRef === undefined) return {};
        const { navbarItem } = assignmentRef;

        // if (columnItem === undefined || column === undefined) return {};
        const newNavbarItem = cloneDeep(navbarItem);

        // `itemType` -> `featured`
        // Only a dropdown menu item in a column that will trigger the change
        // to `featured` type. Hence, we must check if the column exist and
        // the column is not empty.
        if (
          event.payload.type === 'featured'
          && context?.selectedColumnIdx !== undefined
          && !isColumnEmpty(
            newNavbarItem.menu.children[context?.selectedColumnIdx] as NavbarDropdownMenuColumn,
          )
        ) {
          const selectedDropdownMenuItem = newNavbarItem.menu.children[
            context?.selectedColumnIdx
          ].children[context?.selectedColumnItemIdx] as NavbarDropdownMenuItem;
          const currentColumn = newNavbarItem.menu.children[
            context?.selectedColumnIdx
          ] as NavbarDropdownMenuColumn;
          const previousFeaturedItem = newNavbarItem.menu.children[0].type === 'navbar_dropdown_menu_item'
            ? newNavbarItem.menu.children[0] as NavbarDropdownMenuItem
            : undefined;

          // If the previous featured item exist, then we need to move it to a new column
          if (previousFeaturedItem) {
            // Since we are moving the previous featured item to a new column,
            // we need to know what is the current existing item type.
            const newColumn: NavbarDropdownMenuColumn = makeInitialMenuColumnValue();
            previousFeaturedItem.itemType = selectedDropdownMenuItem.itemType;
            newColumn.children = [previousFeaturedItem];
            newNavbarItem.menu.children.splice(0, 1, newColumn);
          }

          // Create a copy of the selected dropdownMenuItem
          // then change its type to `featured`.
          const newFeaturedDropdownMenuItem = cloneDeep(selectedDropdownMenuItem);
          newFeaturedDropdownMenuItem.itemType = event.payload.type;

          // Create new column item
          const newFeaturedColumnItem: NavbarDropdownMenuItem = {
            ...selectedDropdownMenuItem,
            itemType: event.payload.type, // "featured"
          };

          // Remove the current featured item from the column
          newNavbarItem.menu.children[context?.selectedColumnIdx].children = (
            currentColumn.children as NavbarDropdownMenuItem[]
          ).filter((item) => item.id !== newFeaturedColumnItem.id);

          // Add the current featured item as the first item of the
          // navbar item
          newNavbarItem.menu.children = [
            newFeaturedColumnItem,
            ...newNavbarItem.menu.children,
          ] as [NavbarDropdownMenuItem, ...NavbarDropdownMenuColumn[]];

          const newMenu = updateArrayItemByPosition(
            context.dataNavbar[context.menuType],
            context.menuIndex ?? -1,
            newNavbarItem,
          );

          return {
            dataNavbar: {
              ...context.dataNavbar,
              [context.menuType]: newMenu,
            },
          };
        }

        if (event.payload.type === 'featured'
          && context?.selectedColumnIdx === undefined) return {};

        // Handle other than 'featured'
        // Update all children of the menu
        const newChildren = newNavbarItem.menu.children.map((child, idx) => {
          // Since we might be a tuple of [NavbarDropdownMenuItem, ...NavbarDropdownMenuColumn[]]
          // We can skip if the type of the element is  NavbarDropdownMenuItem
          // Ref: see the type of NavbarDropdown['menu']['children'].
          if (idx === 0 && child.type === 'navbar_dropdown_menu_item' && context?.selectedColumnIdx === undefined) {
            const newColumn = makeInitialMenuColumnValue();
            const newDropdownMenuItem = cloneDeep(child) as NavbarDropdownMenuItem;
            newDropdownMenuItem.itemType = event.payload.type;
            newColumn.children = [newDropdownMenuItem];
            return newColumn;
          }

          // Skip if the trigger coming from a NavbarDropdownMenuItem
          // inside a NavbarDropdownMenuColumn
          if (idx === 0 && child.type === 'navbar_dropdown_menu_item' && context?.selectedColumnIdx !== undefined) {
            return child;
          }

          if (isColumnEmpty(child as NavbarDropdownMenuColumn)) {
            return child;
          }
          const newColumn = cloneDeep(child) as NavbarDropdownMenuColumn;

          newColumn.children = (newColumn.children as NavbarDropdownMenuItem[]).map((item) => ({
            ...item,
            itemType: event.payload.type,
          }));

          return newColumn;
        }) as NavbarDropdown['menu']['children'];

        newNavbarItem.menu.children = newChildren;

        const newMenu = updateArrayItemByPosition(
          context.dataNavbar[context.menuType],
          context.menuIndex ?? -1,
          newNavbarItem,
        );

        return {
          dropdownColumnItemType: event.payload.type,
          dataNavbar: {
            ...context.dataNavbar,
            [context.menuType]: newMenu,
          },
        };
      }),
      assignColumnItemFontToContext: assign((context, event) => {
        if (
          context.dataNavbar === undefined
          || context.menuType === undefined
          || context.menuType === 'brand'
          || context.dataNavbar?.[context.menuType] === undefined) return {};

        // Get and check the current selected column
        const assignmentRef = getSelectedNavbarDropdownItems(context);
        if (assignmentRef === undefined) return {};

        const { columnItem } = assignmentRef;

        if (columnItem === undefined) return {};

        const newDropdownMenuItem: NavbarDropdownMenuItem = {
          ...columnItem,
          fontFamily: event.payload.font,
        };

        const updatedMenuItem = updateDropdownMenuItem({
          context,
          newDropdownMenuItem,
        });

        if (updatedMenuItem === undefined) return {};

        return {
          dataNavbar: {
            ...context.dataNavbar,
            [context.menuType]: updatedMenuItem,
          },
        };
      }),
      assignNewColumnItemToContext: assign((context, event) => {
        if (event.type === 'ON_DELETE_COLUMN_ITEM') {
          if (
            context.dataNavbar === undefined
            || context.menuType === undefined
            || context.menuType === 'brand'
            || context.dataNavbar?.[context.menuType] === undefined
            || context.menuIndex === undefined
            || context.dataNavbar?.[context.menuType][context.menuIndex] === undefined
            || context?.selectedColumnItemIdx === undefined
          ) return {};

          const assignmentRef = getSelectedNavbarDropdownItems(context);
          if (assignmentRef === undefined) return {};

          const { navbarItem } = assignmentRef;

          const clonedNavbarItem = cloneDeep(navbarItem);

          // If the column reference exist, we assume that
          // the dropdown menu is in a column
          if (context?.selectedColumnIdx !== undefined
            && (!isColumnEmpty(
              clonedNavbarItem.menu.children[context.selectedColumnIdx] as NavbarDropdownMenuColumn,
            ))
          ) {
            // eslint-disable-next-line max-len
            clonedNavbarItem.menu.children[
              context.selectedColumnIdx
            ].children.splice(
              context.selectedColumnItemIdx,
              1,
            ) as NavbarDropdownMenuItem[];

            const newMenu = [...context.dataNavbar[context.menuType]];
            newMenu[context.menuIndex] = clonedNavbarItem;

            return {
              dataNavbar: {
                ...context.dataNavbar,
                [context.menuType]: newMenu,
              },
            };
          }

          clonedNavbarItem.menu.children.splice(
            context.selectedColumnItemIdx,
            1,
          ) as NavbarDropdownMenuColumn[];

          const newMenu = [...context.dataNavbar[context.menuType]];
          newMenu[context.menuIndex] = clonedNavbarItem;

          return {
            dataNavbar: {
              ...context.dataNavbar,
              [context.menuType]: newMenu,
            },
          };
        }

        if (event.type === 'ON_ADD_COLUMN_ITEM') {
          if (
            context.dataNavbar === undefined
            || context.menuType === undefined
            || context.menuType === 'brand'
            || context.dataNavbar?.[context.menuType] === undefined) return {};
          // Get and check the current selected column
          const assignmentRef = getSelectedNavbarDropdownItems(context);
          if (assignmentRef === undefined) return {};

          const { navbarItem } = assignmentRef;

          const currentMenuItemChildren: NavbarDropdown['menu']['children'] = navbarItem.menu.children;

          // We wanted to insert the new column item into the selected column
          // or into the last column if there's no selected column.
          let columnTargetIndex = currentMenuItemChildren.findIndex((c) => c.id === context?.selectedColumnId ?? '');
          if (columnTargetIndex === -1) {
            columnTargetIndex = currentMenuItemChildren.length > 0
              ? currentMenuItemChildren.length - 1
              : 0;
          }

          // eslint-disable-next-line max-len
          const currentColumn = currentMenuItemChildren[columnTargetIndex] as NavbarDropdownMenuColumn;

          // If there's a selected column item, we want to insert the new column item
          // after the selected column item
          if (context?.selectedColumnItemId) {
            // eslint-disable-next-line max-len
            const selectedColumnItemIndex = (currentColumn.children as NavbarDropdownMenuItem[]).findIndex(
              (item) => item.id === context.selectedColumnItemId,
            );
            if (selectedColumnItemIndex !== -1) {
              currentColumn.children.splice(
                selectedColumnItemIndex + 1,
                0,
                makeInitialMenuItemValue(context?.dropdownColumnItemType),
              );
            }
          } else {
            currentColumn.children.splice(
              currentColumn.children.length + 1,
              0,
              makeInitialMenuItemValue(context?.dropdownColumnItemType),
            );
          }

          navbarItem.menu.children = currentMenuItemChildren;

          // Create a copy of the middle menu group
          const newMiddleMenuItem: NavbarDropdown = {
            ...navbarItem,
            menu: {
              ...navbarItem.menu,
            },
          };

          // pdate the middle menu group
          const newMiddleMenuArr = updateArrayItemByPosition(
            context.dataNavbar.menu,
            context.menuIndex ?? -1,
            newMiddleMenuItem,
          );

          return {
            dataNavbar: {
              ...context.dataNavbar,
              [context.menuType]: newMiddleMenuArr,
            },
          };
        }

        return {};
      }),
      assignDroppedColumnItemToContext: assign((context, event) => {
        const {
          navbarMenuItemType,
          navbarMenuItemIndex,
          dropTargetColumnId,
          dropTargetId,
          dropDirection,
          dragSourceColumnId,
          dragSourceId,
        } = event.payload;

        if (
          context.dataNavbar === undefined
          || context.menuType === 'brand'
          || context.dataNavbar[navbarMenuItemType] === undefined
          || context.dataNavbar[navbarMenuItemType][navbarMenuItemIndex] === undefined) return {};

        // Create a copy of the navbar menu item
        const newNavbarItem = {
          ...context.dataNavbar[navbarMenuItemType][navbarMenuItemIndex],
        } as NavbarDropdown;

        const currentMenuItemChildren: NavbarDropdown['menu']['children'] = newNavbarItem.menu.children;
        // =====================================================
        // Remove the source column item from its column
        // =====================================================
        const sourceColumnIndex = currentMenuItemChildren.findIndex((c) => c.id === dragSourceColumnId ?? '');
        if (sourceColumnIndex === -1) {
          return {};
        }

        const sourceColumn = currentMenuItemChildren[sourceColumnIndex] as NavbarDropdownMenuColumn;
        const sourceColumnItemIndex = (sourceColumn.children as NavbarDropdownMenuItem[]).findIndex(
          (item) => item.id === dragSourceId,
        );
        const sourceColumnItem = (sourceColumn.children as NavbarDropdownMenuItem[]).find(
          (item) => item.id === dragSourceId,
        );
        sourceColumn.children.splice(sourceColumnItemIndex, 1);

        // =====================================================
        // Insert the source column item into the
        // selected column
        // =====================================================
        // We wanted to insert the new column item into the selected column
        // or into the last column if there's no selected column.
        const targetColumnIndex = currentMenuItemChildren.findIndex((c) => c.id === dropTargetColumnId ?? '');
        if (targetColumnIndex === -1) {
          return {};
        }

        // eslint-disable-next-line max-len
        const targetColumn = currentMenuItemChildren[targetColumnIndex] as NavbarDropdownMenuColumn;
        const targetColumnItemIndex = (targetColumn.children as NavbarDropdownMenuItem[]).findIndex(
          (item) => item.id === dropTargetId,
        );

        if (
          dropDirection === undefined
          || dropDirection === 'left'
          || dropDirection === 'right') {
          return {};
        }

        if (dropDirection === 'top') {
          targetColumn.children.splice(
            targetColumnItemIndex,
            0,
            sourceColumnItem as NavbarDropdownMenuItem,
          );
        }
        if (dropDirection === 'bottom') {
          targetColumn.children.splice(
            targetColumnItemIndex + 1,
            0,
            sourceColumnItem as NavbarDropdownMenuItem,
          );
        }

        // =====================================================
        // Update the navbar
        // =====================================================
        const newMiddleMenuArr = updateArrayItemByPosition(
          context.dataNavbar.menu,
          context.menuIndex ?? -1,
          newNavbarItem,
        );

        return {
          dataNavbar: {
            ...context.dataNavbar,
            [navbarMenuItemType]: newMiddleMenuArr,
          },
        };
      }),
      assignDroppedColumnToContext: assign((context, event) => {
        const {
          navbarMenuItemType,
          navbarMenuItemIndex,
          dropTargetColumnId,
          dropDirection,
          dragSourceColumnId,
        } = event.payload;

        if (
          context.dataNavbar === undefined
          || context.menuType === 'brand'
          || context.dataNavbar[navbarMenuItemType] === undefined
          || context.dataNavbar[navbarMenuItemType][navbarMenuItemIndex] === undefined) return {};

        // Create a copy of the navbar menu item
        const newNavbarItem = {
          ...context.dataNavbar[navbarMenuItemType][navbarMenuItemIndex],
        } as NavbarDropdown;

        const currentMenuItemChildren: NavbarDropdown['menu']['children'] = newNavbarItem.menu.children;

        // =====================================================
        // Remove the source column item from its column
        // =====================================================
        const sourceColumnIndex = currentMenuItemChildren.findIndex((c) => c.id === dragSourceColumnId ?? '');
        if (sourceColumnIndex === -1) {
          return {};
        }

        const sourceColumn = currentMenuItemChildren[sourceColumnIndex] as NavbarDropdownMenuColumn;

        currentMenuItemChildren.splice(sourceColumnIndex, 1);

        // =====================================================
        // Insert the source column item into the
        // selected column
        // =====================================================
        // We wanted to insert the new column item into the selected column
        // or into the last column if there's no selected column.
        const targetColumnIndex = currentMenuItemChildren.findIndex((c) => c.id === dropTargetColumnId ?? '');
        if (targetColumnIndex === -1) {
          return {};
        }

        if (
          dropDirection === undefined
          || dropDirection === 'bottom'
          || dropDirection === 'top') {
          return {};
        }

        if (dropDirection === 'left') {
          currentMenuItemChildren.splice(
            targetColumnIndex,
            0,
            sourceColumn,
          );
        }
        if (dropDirection === 'right') {
          currentMenuItemChildren.splice(
            targetColumnIndex + 1,
            0,
            sourceColumn,
          );
        }

        // =====================================================
        // Update the navbar
        // =====================================================
        const newMiddleMenuArr = updateArrayItemByPosition(
          context.dataNavbar.menu,
          context.menuIndex ?? -1,
          newNavbarItem,
        );

        return {
          dataNavbar: {
            ...context.dataNavbar,
            [navbarMenuItemType]: newMiddleMenuArr,
          },
        };
      }),
      assignSelectedColumnToContext: assign((context, event) => {
        if (event.type === 'ON_MOUSE_ENTER_ITEM' && event.payload.state === true) {
          const { columnId, columnItemId } = event.payload;
          if (
            context?.dataNavbar === undefined
            || context?.menuIndex === undefined
            || context?.menuType === undefined
            || context?.menuType === 'brand'
          ) return {};

          const currentMenuItem = context
            .dataNavbar[context.menuType][context.menuIndex] as NavbarDropdown;

          // User selected a column item
          if (
            columnId !== undefined
            && columnItemId !== undefined
            && event.payload.dropdownItemType === 'column_item'
          ) {
            const columnIdx = currentMenuItem.menu.children.findIndex(
              (c) => c.id === columnId,
            );
            const columnItemIdx = (
              currentMenuItem.menu.children[columnIdx] as NavbarDropdownMenuColumn
            ).children.findIndex(
              (c) => c.id === columnItemId,
            );

            return {
              selectedColumnId: event.payload.columnId,
              selectedDropdownItemType: event.payload.dropdownItemType,
              selectedColumnItemId: event.payload.columnItemId,
              selectedColumnIdx: columnIdx,
              selectedColumnItemIdx: columnItemIdx,
            };
          }

          // User selected a featured item
          if (
            columnId === undefined
            && columnItemId !== undefined
          ) {
            const columnItemIdx = currentMenuItem.menu.children.findIndex(
              (c) => c.id === columnItemId,
            );

            return {
              selectedColumnId: undefined,
              selectedColumnItemId: undefined,
              selectedColumnItemIdx: columnItemIdx,
              selectedColumnIdx: undefined,
              selectedDropdownItemType: event.payload.dropdownItemType,
            };
          }

          // User selected a column
          const columnIdx = currentMenuItem.menu.children.findIndex(
            (c) => c.id === columnId,
          );
          return {
            selectedColumnId: event.payload.columnId,
            selectedColumnIdx: columnIdx,
            selectedDropdownItemType: event.payload.dropdownItemType,
            selectedColumnItemId: undefined,
          };
        }

        if (
          (event.type === 'ON_MOUSE_LEAVE_ITEM' && event.payload.state === false)
          || (event.type === 'ON_DELETE_COLUMN_ITEM')
        ) {
          return {
            selectedColumnId: undefined,
            selectedColumnItemId: undefined,
          };
        }

        return {};
      }),
      assignNewColumnToContext: assign((context, event) => {
        if (
          context.dataNavbar === undefined
          || context.menuType === undefined
          || context.menuType === 'brand'
          || context.menuIndex === undefined
          || context.dataNavbar[context.menuType] === undefined
          || context.dataNavbar[context.menuType][context.menuIndex] === undefined
        ) return {};

        const assignmentRef = getSelectedNavbarDropdownItems(context);
        if (assignmentRef === undefined) return {};
        const { navbarItem } = assignmentRef;
        const clonedNavbarItem = cloneDeep(navbarItem);

        // Adding new column from "Add item inside" button in the empty column UI.
        if (event.type === 'ON_ADD_NEW_COLUMN_ITEM') {
          if (
            context.dataNavbar === undefined
            || context.menuType === undefined
            || context.dataNavbar?.[context.menuType] === undefined) return {};

          const currentMenuItemChildren: NavbarDropdown['menu']['children'] = navbarItem.menu.children;

          // We wanted to insert the new column item into the selected column
          // or into the last column if there's no selected column.
          let columnTargetIndex = currentMenuItemChildren.findIndex((c) => c.id === event.payload.columnId ?? '');
          if (columnTargetIndex === -1) {
            columnTargetIndex = currentMenuItemChildren.length > 0
              ? currentMenuItemChildren.length - 1
              : 0;
          }

          // eslint-disable-next-line max-len
          const currentColumn = currentMenuItemChildren[columnTargetIndex] as NavbarDropdownMenuColumn;

          // TODO(@ibedwi): Add condition if `context?.selectedColumnItemIndex` is determined
          currentColumn.children = [
            // TODO(@ibedwi): add context so we can create the correct menu item type
            makeInitialMenuItemValue(context?.dropdownColumnItemType),
          ];

          navbarItem.menu.children = currentMenuItemChildren;

          // Create a copy of the middle menu group
          const newMenuItem: NavbarDropdown = {
            ...navbarItem,
            menu: {
              ...navbarItem.menu,
            },
          };

          // pdate the middle menu group
          const newMenuArr = updateArrayItemByPosition(
            context.dataNavbar.menu,
            context.menuIndex ?? -1,
            newMenuItem,
          );

          return {
            dataNavbar: {
              ...context.dataNavbar,
              [context.menuType]: newMenuArr,
            },
          };
        }

        if (context?.selectedColumnIdx === undefined) return {};

        if (event.type === 'ON_DELETE_COLUMN') {
          clonedNavbarItem.menu.children.splice(context?.selectedColumnIdx, 1);

          const newMenu = [...context.dataNavbar[context.menuType]];
          newMenu[context.menuIndex] = clonedNavbarItem;

          return {
            dataNavbar: {
              ...context.dataNavbar,
              [context.menuType]: newMenu,
            },
          };
        }

        if (event.type === 'ON_ADD_COLUMN') {
          clonedNavbarItem.menu.children.push(makeInitialMenuColumnValue());
          const newMenu = [...context.dataNavbar[context.menuType]];
          newMenu[context.menuIndex] = clonedNavbarItem;

          return {
            dataNavbar: {
              ...context.dataNavbar,
              [context.menuType]: newMenu,
            },
          };
        }

        return {};
      }),
      assignNavbarToContext: assign((_context, event) => {
        if (event.type === 'ON_DROPDOWN_OPEN') {
          if (event?.menuType === 'brand') return {};
          // eslint-disable-next-line max-len
          const dropdownColumnChildren = (
            event?.dataNavbar?.[event.menuType][event.menuIndex] as NavbarDropdown
          ).menu?.children as NavbarDropdown['menu']['children'];

          let dropdownColumnItemType: NavbarDropdownMenuItem['itemType'] | undefined;
          if (dropdownColumnChildren !== undefined && dropdownColumnChildren.length > 0) {
            if (
              dropdownColumnChildren[0].type === 'navbar_dropdown_menu_item'
              && dropdownColumnChildren.length > 1
              && dropdownColumnChildren[1].children.length > 0
              && !('text' in dropdownColumnChildren[1].children[0])) {
              dropdownColumnItemType = dropdownColumnChildren[1].children[0].itemType;
            } else {
              dropdownColumnItemType = 'long';
            }
          }

          return {
            dropdownColumnItemType,
            dataNavbar: event?.dataNavbar,
            menuType: event?.menuType,
            menuIndex: event?.menuIndex,
          };
        }

        if (event.type === 'ON_DROPDOWN_CLOSE') {
          setDomNode(null);
          setItemDomNode(null);
          return {
            dropdownColumnItemType: undefined,
            dataNavbar: undefined,
            menuType: undefined,
            menuIndex: undefined,
          };
        }

        return {};
      }),
      assignStyleToContext: assign((context, event) => {
        if (
          context.dataNavbar
          && context.menuType === 'menu'
          && context.menuIndex !== undefined
        ) {
          const currentMenuItem = context.dataNavbar.menu[context.menuIndex] as NavbarDropdown;
          const newMiddleMenuItem: NavbarDropdown = {
            ...currentMenuItem,
            menu: {
              ...currentMenuItem.menu,
              style: event.style,
            },
          };

          const newMiddleMenuArr = updateArrayItemByPosition(
            context.dataNavbar.menu,
            context.menuIndex ?? -1,
            newMiddleMenuItem,
          );

          return {
            dataNavbar: {
              ...context.dataNavbar,
              menu: newMiddleMenuArr,
            },
          };
        }

        return {};
      }),
      effectUpdateSlateNode: (context) => {
        const nodeReference = findNodePath(editor, navbarReference as TNode);
        changeNavbar(editor, nodeReference!, context.dataNavbar);
      },
    },
    guards: {
      isColumn: (context) => context?.selectedDropdownItemType === 'column',
    },
    services: {
      uploadImage: async (_, event) => {
        const url = await editor!.fileManager!.upload(event.newFile);
        return url;
      },
    },
  });

  const toggleDropdownContainerMenuHandler = (
    {
      ref,
      state,
      nav,
      menuIndex,
      type,
    }: TextEditorOpenNavbarDropdownContainerEvent,
  ) => {
    if (state) {
      // Reset state to idle
      machineSend({
        type: 'ON_DROPDOWN_CLOSE',
      });
      machineSend({
        type: 'ON_DROPDOWN_OPEN',
        dataNavbar: nav,
        menuIndex,
        menuType: type,
      });
      setDomNode(ref);
      setNavbarReference(nav);
    } else {
      machineSend({
        type: 'ON_DROPDOWN_CLOSE',
      });
      setDomNode(null);
    }
  };

  const closeDropdownContainerMenuHandler = () => {
    setDomNode(null);
    machineSend({ type: 'ON_DROPDOWN_CLOSE' });
  };

  const handleHoverMenuItems = (payload: ToolbarNavbarDropdownMenuItemEvent) => {
    const { state } = payload;
    if (state === true) {
      // Reset state to idle
      machineSend({
        type: 'ON_MOUSE_LEAVE_ITEM',
        payload: {
          state: false,
        },
      });
      machineSend({
        type: 'ON_MOUSE_ENTER_ITEM',
        payload,
      });
      setItemDomNode(payload.ref);
    } else {
      machineSend({
        type: 'ON_MOUSE_LEAVE_ITEM',
        payload,
      });
    }
  };

  const handleAddNewColumnItem = (payload: ToolbarNavbarDropdownMenuEmptyColumnAddEvent) => {
    machineSend({ type: 'ON_ADD_NEW_COLUMN_ITEM', payload });
  };

  const handleDropColumnItem = (payload: ToolbarNavbarDropdownMenuItemDropEvent) => {
    machineSend({ type: 'ON_DROP_COLUMN_ITEM', payload });
  };

  const handleDropColumn = (payload: ToolbarNavbarDropdownColumnDropEvent) => {
    machineSend({ type: 'ON_DROP_COLUMN', payload });
  };

  const handleDeletedMenu = () => {
    machineSend({ type: 'ON_MENU_DELETED' });
  };

  useEffect(() => {
    editor.emitter.on('toolbar:navbar:dropdown-menu-item:hover', handleHoverMenuItems);
    editor.emitter.on('toolbar:navbar:dropdown-empty-menu-column:add', handleAddNewColumnItem);
    editor.emitter.on('toolbar:navbar:dropdown-menu-item:drop', handleDropColumnItem);
    editor.emitter.on('toolbar:navbar:dropdown-column:drop', handleDropColumn);
    editor.emitter.on('toolbar:navbar:menu:delete', handleDeletedMenu);
    editor.emitter.on('toolbar:navbar:delete-navbar', handleDeletedMenu);
    editor.emitter.on('toolbar:navbar:dropdown:menu:open', toggleDropdownContainerMenuHandler);
    editor.emitter.on('toolbar:navbar:dropdown:menu:close', closeDropdownContainerMenuHandler);

    return () => {
      editor.emitter.off('toolbar:navbar:dropdown:menu:open', toggleDropdownContainerMenuHandler);
      editor.emitter.off('toolbar:navbar:dropdown-menu-item:hover', handleHoverMenuItems);
      editor.emitter.off('toolbar:navbar:dropdown-empty-menu-column:add', handleAddNewColumnItem);
      editor.emitter.off('toolbar:navbar:dropdown-menu-item:drop', handleDropColumnItem);
      editor.emitter.off('toolbar:navbar:dropdown-column:drop', handleDropColumn);
      editor.emitter.off('toolbar:navbar:menu:delete', handleDeletedMenu);
      editor.emitter.off('toolbar:navbar:delete-navbar', handleDeletedMenu);
      editor.emitter.off('toolbar:navbar:dropdown:menu:close', closeDropdownContainerMenuHandler);
    };
  }, []);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {
        machineState.hasTag('ui:show_bottom_options')
        && domNode
        && (
          <OverlayPortal>
            <Floating
              reference={domNode}
              options={{
                placement: 'bottom',
                middleware: [
                  flip(),
                  shift({
                    crossAxis: true,
                  }),
                ],
              }}
              autoUpdate
              style={{
                zIndex: 6,
                width: '100%',
              }}
            >
              <BottomOptions
                machineService={machineService}
              />
            </Floating>
          </OverlayPortal>
        )
      }
      {
        machineState.hasTag('ui:show_hovered_border')
        && itemDomNode
        && (
          <OverlayPortal>
            <Floating
              reference={itemDomNode}
              options={{
                placement: 'top-start',
              }}
              autoUpdate
              style={{
                zIndex: 6,
                width: '100%',
                position: 'relative',
              }}
            >
              <div style={{
                position: 'absolute',
                outline: '2px solid var(--background-highlight-highlight)',
                width: itemDomNode?.offsetWidth,
                height: itemDomNode?.offsetHeight,
                overflow: 'visible',
              }}
              >
                {
                  machineState?.context?.selectedDropdownItemType === 'column'
                    ? <ColumnSettingToolbar machineActor={machineService} />
                    : <ColumnItemSettingToolbar machineActor={machineService} />
                }
              </div>
            </Floating>
          </OverlayPortal>
        )
      }
    </>
  );
}
